import React from 'react';
import { AlertTriangle, CheckCircle, Truck } from 'lucide-react';
import { NetworkPanel } from '../../NetworkPanel';
import type {
  ContactMatchData,
  ShellGameAlert,
  VINMatch,
} from '../CarrierTabs.types';

export interface NetworkProps {
  dotNumber: number;
  contactMatches?: ContactMatchData | null;
  shellGameAlerts?: ShellGameAlert[];
  setSelectedVin: (vin: string | null) => void;
}

const Network: React.FC<NetworkProps> = ({
  dotNumber,
  contactMatches,
  shellGameAlerts = [],
  setSelectedVin,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Contact Information Matches Section */}
      {contactMatches && contactMatches.summary && contactMatches.summary.totalMatches > 0 && (
        <div className="card-modern fade-in-up">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
            <AlertTriangle size={24} style={{ color: 'var(--color-warning)' }} />
            <h3 className="heading-3">Contact Information Matches</h3>
          </div>

          {/* Summary Stats */}
          <div className="card-modern" style={{ marginBottom: 'var(--space-lg)', background: '#f1e2c2' }}>
            <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
              <AlertTriangle size={24} style={{ color: '#b07d2b' }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>
                  {contactMatches.summary.totalMatches} Total Matches Found
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                  {contactMatches.summary.priorityMatches > 0 && (
                    <><strong>{contactMatches.summary.priorityMatches} CRITICAL</strong> (Multiple match types) | </>
                  )}
                  {contactMatches.summary.vinMatches > 0 && (
                    <>{contactMatches.summary.vinMatchedVinsCount || contactMatches.summary.vinMatches} VIN matches | </>
                  )}
                  {contactMatches.summary.phoneMatches + contactMatches.summary.emailMatches + contactMatches.summary.officerMatches + contactMatches.summary.addressMatches} Contact matches
                </div>
              </div>
            </div>
          </div>

          {/* Match Type Badges */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-lg)' }}>
            {contactMatches.summary.priorityMatches > 0 && (
              <span className="badge-modern badge-danger" style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}>
                {contactMatches.summary.priorityMatches} CRITICAL MATCH{contactMatches.summary.priorityMatches > 1 ? 'ES' : ''} (Multiple Criteria)
              </span>
            )}
            {contactMatches.summary.vinMatches > 0 && (
              <span className="badge-modern badge-warning" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {contactMatches.summary.vinMatchedVinsCount || contactMatches.summary.vinMatches} VIN Match{(contactMatches.summary.vinMatchedVinsCount || contactMatches.summary.vinMatches) > 1 ? 'es' : ''} with {contactMatches.summary.vinMatchedCompaniesCount || contactMatches.summary.vinMatches} compan{(contactMatches.summary.vinMatchedCompaniesCount || contactMatches.summary.vinMatches) > 1 ? 'ies' : 'y'}
              </span>
            )}
            {contactMatches.summary.phoneMatches > 0 && (
              <span className="badge-modern badge-danger">
                Phone shared with {contactMatches.summary.phoneMatches} carrier{contactMatches.summary.phoneMatches > 1 ? 's' : ''}
              </span>
            )}
            {contactMatches.summary.emailMatches > 0 && (
              <span className="badge-modern badge-warning">
                Email shared with {contactMatches.summary.emailMatches} carrier{contactMatches.summary.emailMatches > 1 ? 's' : ''}
              </span>
            )}
            {contactMatches.summary.officerMatches > 0 && (
              <span className="badge-modern badge-warning">
                Officer shared with {contactMatches.summary.officerMatches} carrier{contactMatches.summary.officerMatches > 1 ? 's' : ''}
              </span>
            )}
            {contactMatches.summary.addressMatches > 0 && (
              <span className="badge-modern badge-danger">
                Address shared with {contactMatches.summary.addressMatches} carrier{contactMatches.summary.addressMatches > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* VIN Matches Details */}
          {contactMatches.matches?.vins && contactMatches.matches.vins.length > 0 && (
            <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--color-gray-200)' }}>
              <h4 className="heading-3" style={{ marginBottom: 'var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <Truck size={18} style={{ color: 'var(--color-warning)' }} />
                VIN Matches ({contactMatches.summary.vinMatchedVinsCount || contactMatches.matches.vins.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {contactMatches.matches.vins.map((match: VINMatch, idx: number) => (
                  <div key={idx} className="card-modern" style={{ borderLeft: '3px solid var(--color-warning)' }}>
                    <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'start', marginBottom: 'var(--space-sm)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 'var(--space-xs)' }}>
                          {match.legalName}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginBottom: 'var(--space-sm)' }}>
                          DOT {match.dotNumber} | Status: {match.status === 'A' ? 'Active' : 'Inactive'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                          {match.matchCount} shared vehicle{match.matchCount > 1 ? 's' : ''} ({match.percentageOfFleet}% of their fleet)
                        </div>
                      </div>
                    </div>
                    {/* Matched VINs */}
                    <div style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--color-gray-200)' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-500)', marginBottom: 'var(--space-xs)', textTransform: 'uppercase' }}>
                        Shared VINs:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                        {match.matchedVins?.map((vin: string, vinIdx: number) => {
                          // Check if this VIN is a shell game alert
                          const shellGameAlert = (shellGameAlerts as any[]).find((alert: any) => alert.vin === vin);
                          const isCriticalShellGame = shellGameAlert && (
                            shellGameAlert.riskLevel === 'CRITICAL' ||
                            shellGameAlert.riskLevel === 'HIGH'
                          );

                          return (
                            <span
                              key={vinIdx}
                              onClick={() => setSelectedVin && setSelectedVin(vin)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: isCriticalShellGame ? 'rgba(179, 65, 44, 0.1)' : 'rgba(176, 125, 43, 0.1)',
                                border: isCriticalShellGame ? '1px solid rgba(179, 65, 44, 0.5)' : '1px solid rgba(176, 125, 43, 0.3)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: isCriticalShellGame ? '#b3412c' : 'var(--color-warning-dark)',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              title={isCriticalShellGame ? `SHELL GAME ALERT: ${shellGameAlert?.totalCarriers} carriers` : 'Click to view VIN details'}
                            >
                              {vin}
                              {isCriticalShellGame && ' ⚠️'}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note: Contact matches modal is handled in parent component */}
        </div>
      )}

      {/* No Contact Matches Message */}
      {(!contactMatches || !contactMatches.summary || contactMatches.summary.totalMatches === 0) && (
        <div className="card-modern fade-in-up" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
          <CheckCircle size={48} style={{ color: 'var(--color-success)', marginBottom: 'var(--space-md)' }} />
          <h3 className="heading-3" style={{ marginBottom: 'var(--space-sm)' }}>No Contact Information Matches</h3>
          <p className="text-muted">This carrier does not share contact information (phone, email, address, officers) with other carriers.</p>
        </div>
      )}

      {/* Network Panel */}
      <NetworkPanel dotNumber={dotNumber} />
    </div>
  );
};

export default Network;
